import { buildQrLoginUrl, buildQrUserPayload, parseQrSocketMessage } from '../utils/qr-login'

const QR_EXPIRE_SECONDS = 60_000
const QR_HEARTBEAT_SECONDS = 10_000
const QR_RECONNECT_SECONDS = 2_000
const QR_WEBSOCKET_URL = 'wss://websocket.interface003.com'

export type QrLoginStatus = 'idle' | 'loading' | 'ready' | 'scanned' | 'expired' | 'error'

export const useAppQrLogin = () => {
  const appStore = useAppStore()
  const matomo = useMatomo()

  const { invokeV3 } = useLoginApi()
  const { accessCookie } = useLogin()
  const { ensureSelectedCountry } = useLoginCountries()

  const qrUrl = ref('')
  const qrToken = ref('')
  const qrScanSucc = ref(false)
  const refreshQR = ref(false)
  const status = ref<QrLoginStatus>('idle')
  const errorMessage = ref('')

  let ws: WebSocket | null = null
  let qrTimer: ReturnType<typeof setTimeout> | null = null
  let heartTimer: ReturnType<typeof setTimeout> | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let reconnecting = false
  let destroyed = false

  const clearHeartbeat = () => {
    if (heartTimer) {
      clearTimeout(heartTimer)
      heartTimer = null
    }
  }

  const clearExpireTimer = () => {
    if (qrTimer) {
      clearTimeout(qrTimer)
      qrTimer = null
    }
  }

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  const closeSocket = () => {
    clearHeartbeat()
    if (!ws) return

    const currentSocket = ws
    ws = null
    currentSocket.close()
  }

  const refreshExpireTimer = () => {
    clearExpireTimer()
    qrTimer = setTimeout(() => {
      if (destroyed) return
      refreshQR.value = true
      qrScanSucc.value = false
      status.value = 'expired'
      closeSocket()
    }, QR_EXPIRE_SECONDS)
  }

  const heartbeat = () => {
    if (destroyed || !ws || ws.readyState !== WebSocket.OPEN) return

    ws.send(JSON.stringify({ type: 'ping' }))
    heartTimer = setTimeout(heartbeat, QR_HEARTBEAT_SECONDS)
  }

  const fetchUserAndLogin = async (userId: string) => {
    if (destroyed || !userId) return

    const result = await invokeV3(
      'usercenter$getuserv5',
      buildQrUserPayload({
        countryCode: appStore.CountryCode,
        languageCode: appStore.LanguageCode,
        userId,
      }),
    ).catch(() => null)

    if (destroyed) return

    const userInfo = result?.usercenter?.getuserv5?.data
    if (!userInfo) {
      errorMessage.value = result?.usercenter?.getuserv5?.extra || '扫码登录失败，请刷新二维码后重试'
      status.value = 'error'
      return
    }

    matomo.trackEvent('af_login_success', { af_type: 'WikiBit' })
    await accessCookie(userInfo)
  }

  const reconnect = () => {
    if (destroyed || reconnecting || refreshQR.value || !qrToken.value) return

    reconnecting = true
    clearReconnectTimer()
    reconnectTimer = setTimeout(() => {
      reconnecting = false
      initWebSocket()
    }, QR_RECONNECT_SECONDS)
  }

  const initWebSocket = () => {
    if (!import.meta.client || destroyed || ws || !qrToken.value) return

    const socket = new WebSocket(QR_WEBSOCKET_URL)
    ws = socket

    socket.addEventListener('open', () => {
      socket.send(JSON.stringify({ WikiFXQR: { Token: qrToken.value } }))
      heartbeat()
    })
    socket.addEventListener('error', reconnect)
    socket.addEventListener('message', async (event) => {
      if (destroyed) return

      const message = parseQrSocketMessage(event.data)
      if (message.type === 'noop') return

      if (message.type === 'scanned') {
        refreshQR.value = false
        qrScanSucc.value = true
        status.value = 'scanned'
        return
      }

      if (message.type === 'expired') {
        qrScanSucc.value = false
        refreshQR.value = true
        status.value = 'expired'
        closeSocket()
        return
      }

      await fetchUserAndLogin(message.userId)
    })
    socket.addEventListener('close', () => {
      clearHeartbeat()
      if (ws === socket) {
        ws = null
      }
    })
  }

  const fetchQrToken = async () => {
    if (destroyed) return

    status.value = 'loading'
    errorMessage.value = ''
    refreshQR.value = false
    qrScanSucc.value = false
    clearExpireTimer()
    clearReconnectTimer()
    closeSocket()

    const result = await invokeV3('usercenter$getqrcontent', {}).catch(() => null)
    if (destroyed) return

    const qrData = result?.usercenter?.getqrcontent?.data?.WikiFXQR || {}
    qrToken.value = qrData.Token || ''

    if (!qrToken.value) {
      qrUrl.value = ''
      errorMessage.value = result?.usercenter?.getqrcontent?.extra || '二维码获取失败，请稍后重试'
      status.value = 'error'
      return
    }

    qrUrl.value = buildQrLoginUrl({
      host: appStore.host || '',
      newhost: appStore.newhost || 'wikibit',
      suffix: appStore.suffix || 'com',
      languageCode: appStore.LanguageCode,
      countryCode: appStore.CountryCode,
      token: qrToken.value,
      type: qrData.WikiQRType || '',
    })
    status.value = 'ready'
    initWebSocket()
    refreshExpireTimer()
  }

  onMounted(() => {
    ensureSelectedCountry()
    void fetchQrToken()
  })

  onBeforeUnmount(() => {
    destroyed = true
    clearExpireTimer()
    clearReconnectTimer()
    closeSocket()
  })

  return {
    closeSocket,
    errorMessage,
    fetchQrToken,
    qrScanSucc,
    qrToken,
    qrUrl,
    refreshQR,
    status,
  }
}
